import React from 'react';
import {
  Flex,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  IconButton,
  useDisclosure,
  Divider,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  HStack,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  Icon,
  Link as ChakraLink,
  useColorModeValue
} from '@chakra-ui/react';
import { HamburgerIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  FaUser,
  FaUserCog,
  FaUserTie,
  FaTachometerAlt,
  FaKey,
  FaUsers,
  FaPoll,
  FaEnvelope,
  FaClipboardList,
  FaPuzzlePiece,
  FaRegCalendarAlt,
  FaSignOutAlt,
  FaWrench,
  FaNewspaper,
  FaBuilding,
  FaUserFriends,
  FaTable
} from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useTranslation } from 'react-i18next';
import {
  getDefaultAuthenticatedRoute,
  getNetworkMembership,
  getNetworkMemberships,
  getUserRoles,
  isConsultantUser,
  isNetworkUser,
  isOrgScopedUser,
  isOrganizationAdmin,
} from '../../utils/authRoles';
import { getDisplayRole } from '../../utils/displayRole';
import {
  canAccessAdminSection,
  canManageConsultantsAdmin,
  canManageNetwork,
  canViewLeadsAdmin,
} from '../../utils/authorization';

const formatBreadcrumbLabel = (value) =>
  decodeURIComponent(value)
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const createBreadcrumbs = (location, _appBasePath, excludedSegments = []) => {
  const excluded = new Set(excludedSegments);
  const pathnames = location.pathname.split('/').filter(Boolean);
  const isNetworkPath = /^\/app\/network\/\d+(?:\/|$)/.test(location.pathname);

  return pathnames.reduce((breadcrumbs, value, index) => {
    if (excluded.has(value)) return breadcrumbs;

    breadcrumbs.push({
      label: isNetworkPath && value === 'results'
        ? 'School reports'
        : isNetworkPath && value === 'organizations'
        ? 'Schools'
        : formatBreadcrumbLabel(value),
      href: `/${pathnames.slice(0, index + 1).join('/')}`,
    });

    return breadcrumbs;
  }, []);
};

const Navbar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, logout } = useUser();
  const consultantUser = isConsultantUser(user);
  const userRoles = getUserRoles(user);
  const isHkeAdmin = userRoles.includes('hke_admin');
  const consultantAdminUser = userRoles.includes('consultant_admin');
  const networkRouteMatch = location.pathname.match(/^\/app\/network\/(\d+)/);
  const activeNetworkId = networkRouteMatch?.[1] || null;
  const activeNetworkMembership = activeNetworkId ? getNetworkMembership(user, activeNetworkId) : null;
  const networkMemberships = getNetworkMemberships(user);
  const networkUser = isNetworkUser(user);
  const showNetworkNav =
    networkUser &&
    (!consultantUser || Boolean(activeNetworkMembership)) &&
    (!isHkeAdmin || Boolean(activeNetworkMembership));
  const isOrganizationContext =
    location.pathname.startsWith('/app/organization') &&
    !!(user?.organization || user?.user_type === 'tour' || isOrgScopedUser(user) || isHkeAdmin);
  const orgAppUser = isOrganizationContext || isOrgScopedUser(user);
  const orgAdminUser = isOrganizationAdmin(user);
  const isTour = user?.user_type === 'tour' || user?.organization?.tour_mode === true;
  const canAccessAdmin = canAccessAdminSection(user);
  const canManageConsultants = canManageConsultantsAdmin(user);
  const canViewLeads = canViewLeadsAdmin(user);
  const networkBasePath = activeNetworkId ? `/app/network/${activeNetworkId}` : '/app/network';
  const canManageActiveNetwork = activeNetworkId ? canManageNetwork(user, activeNetworkId) : false;
  const appBasePath = showNetworkNav ? networkBasePath : orgAppUser ? '/app/organization' : consultantUser ? '/app/consultant' : '/app';
  const dashboardPath = showNetworkNav ? (activeNetworkId ? networkBasePath : getDefaultAuthenticatedRoute(user)) : orgAppUser ? appBasePath : getDefaultAuthenticatedRoute(user);
  const homeLabel = t('navbar.dashboard');
  const networkAggregateReportMatch = location.pathname.match(
    /^\/app\/network\/\d+\/report\/(overview|organizations|factors|pairing|schools-factors)$/
  );
  const excludedSegments = showNetworkNav
    ? [
        'app',
        'network',
        ...(activeNetworkId ? [activeNetworkId] : []),
        'edit',
        ...(networkAggregateReportMatch ? ['report', 'overview'] : []),
      ]
    : orgAppUser
    ? ['app', 'organization', 'edit']
    : consultantUser
    ? ['app', 'consultant', 'dashboard', 'edit']
    : ['app', 'dashboard', 'admin', 'edit'];
  const breadcrumbs = createBreadcrumbs(location, appBasePath, excludedSegments);
  const isNetworkReportSection = networkAggregateReportMatch && networkAggregateReportMatch[1] !== 'overview';
  const showBreadcrumbs = breadcrumbs.length > 1 || (isNetworkReportSection && breadcrumbs.length > 0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const colorMode = useColorModeValue('light', 'dark');
  const hoverBgColor = useColorModeValue('gray.100', 'gray.700');
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const isDecember = new Date().getMonth() === 11;

  const logoFilename = colorMode === 'dark' ? 'logo-white-md.png' : 'logo-md.png';
  const logoPath = isDecember ? `/images/Christmas/${logoFilename}` : `/images/${logoFilename}`;

  const handleLogout = () => {
    logout();
  };

  return (
    <Flex
      as="nav"
      align="center"
      justify="space-between"
      padding="1rem"
      backgroundColor={bgColor}
      color={textColor}
      position="sticky"
      top="0"
      zIndex="110"
      boxShadow="xs"
      borderBottom="1px solid"
      borderBottomColor={borderColor}
    >
      <Box display={{ base: 'block', md: 'none' }}>
        <img src={logoPath} alt="Company Logo" style={{ width: '100px' }} />
      </Box>
      {showBreadcrumbs ? (
        <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.500" />} display={{ base: 'none', md: 'flex' }}>
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to={dashboardPath}>
              {homeLabel}
            </BreadcrumbLink>
          </BreadcrumbItem>
          {breadcrumbs.map((breadcrumb, index) => (
            <BreadcrumbItem key={index}>
              <BreadcrumbLink as={Link} to={breadcrumb.href}>
                {breadcrumb.label}
              </BreadcrumbLink>
            </BreadcrumbItem>
          ))}
        </Breadcrumb>
      ) : (
        <Box display={{ base: 'none', md: 'block' }} />
      )}

      <Box display={{ base: 'block', md: 'none' }}>
        <IconButton
          icon={<HamburgerIcon />}
          aria-label={t('navbar.menu')}
          onClick={onOpen}
          variant="outline"
        />
      </Box>

      <HStack spacing={4} display={{ base: 'none', md: 'flex' }}>
        {user && (
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<Box as={FaUser} w={4} h={4} borderRadius="full" />}
              size="sm"
              variant="ghost"
              borderRadius="full"
              backgroundColor={bgColor}
              _hover={{ backgroundColor: hoverBgColor }}
              _focusVisible={{ boxShadow: 'outline' }}
            />
            <MenuList>
              <MenuItem>{`${user.first_name} ${user.last_name}`}</MenuItem>
              <Text as="i" ml={3} fontSize="sm" color="gray">Role: {getDisplayRole(user)}</Text>
              <MenuDivider />
              <MenuItem as={Link} to={`${appBasePath}/profile`}>{t('navbar.profile')}</MenuItem>
              <MenuItem as={Link} to={`${appBasePath}/settings`}>{t('navbar.preferences', 'Preferences')}</MenuItem>
              <MenuItem as={Link} to={`${appBasePath}/password`}>{t('navbar.password')}</MenuItem>
              <MenuDivider />
              <MenuItem onClick={handleLogout}>{t('navbar.logout')}</MenuItem>
            </MenuList>
          </Menu>
        )}
      </HStack>

      <Drawer placement="right" onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{t('navbar.menu')}</DrawerHeader>
          <DrawerBody>
            <VStack align="start" spacing={4}>
              <ChakraLink as={Link} to={dashboardPath} onClick={onClose}>
                <Icon as={FaTachometerAlt} mr={2} />
                {homeLabel}
              </ChakraLink>
              {showNetworkNav ? (
                <>
                  {networkMemberships.length > 1 ? (
                    <ChakraLink as={Link} to="/app/network/select" onClick={onClose}>
                      <Icon as={FaBuilding} mr={2} />
                      Switch network
                    </ChakraLink>
                  ) : null}
                  {activeNetworkId ? (
                    <>
                      <ChakraLink as={Link} to={`${networkBasePath}/report/organizations`} onClick={onClose}>
                        <Icon as={FaUsers} mr={2} />
                        Schools
                      </ChakraLink>
                      <ChakraLink as={Link} to={`${networkBasePath}/results`} onClick={onClose}>
                        <Icon as={FaPoll} mr={2} />
                        School reports
                      </ChakraLink>
                      <ChakraLink as={Link} to={`${networkBasePath}/report/pairing`} onClick={onClose}>
                        <Icon as={FaUserFriends} mr={2} />
                        Pairing
                      </ChakraLink>
                      <ChakraLink as={Link} to={`${networkBasePath}/report/factors`} onClick={onClose}>
                        <Icon as={FaNewspaper} mr={2} />
                        Factors
                      </ChakraLink>
                      <ChakraLink as={Link} to={`${networkBasePath}/report/schools-factors`} onClick={onClose}>
                        <Icon as={FaTable} mr={2} />
                        Schools × Factors
                      </ChakraLink>
                      {canManageActiveNetwork ? (
                        <>
                          <ChakraLink as={Link} to={`${networkBasePath}/users`} onClick={onClose}>
                            <Icon as={FaUsers} mr={2} />
                            Users
                          </ChakraLink>
                          <ChakraLink as={Link} to={`${networkBasePath}/network-settings`} onClick={onClose}>
                            <Icon as={FaWrench} mr={2} />
                            Network Settings
                          </ChakraLink>
                        </>
                      ) : null}
                    </>
                  ) : null}
                </>
              ) : orgAppUser ? (
                <>
                  {orgAdminUser && !isTour ? (
                    <ChakraLink as={Link} to="/app/organization/users" onClick={onClose}>
                      <Icon as={FaUser} mr={2} />
                      {t('organization.users', 'Users')}
                    </ChakraLink>
                  ) : null}
                  <ChakraLink as={Link} to="/app/organization/surveys" onClick={onClose}>
                    <Icon as={FaPoll} mr={2} />
                    {t('organization.surveys', 'Surveys')}
                  </ChakraLink>
                  <ChakraLink as={Link} to="/app/organization/results" onClick={onClose}>
                    <Icon as={FaUsers} mr={2} />
                    {t('organization.results', 'Results')}
                  </ChakraLink>
                  <ChakraLink as={Link} to="/app/organization/workbook" onClick={onClose}>
                    <Icon as={FaClipboardList} mr={2} />
                    {t('organization.workbook.title', 'Workbook')}
                  </ChakraLink>
                  {orgAdminUser && !isTour ? (
                    <ChakraLink as={Link} to="/app/organization/organization-settings" onClick={onClose}>
                      <Icon as={FaWrench} mr={2} />
                      {t('organization.settings', 'School Settings')}
                    </ChakraLink>
                  ) : null}
                </>
              ) : consultantUser ? (
                <>
                  <ChakraLink as={Link} to="/app/consultant/organizations" onClick={onClose}>
                    <Icon as={FaUserTie} mr={2} />
                    {t('navbar.organization')}
                  </ChakraLink>
                  <ChakraLink as={Link} to="/app/consultant/networks" onClick={onClose}>
                    <Icon as={FaBuilding} mr={2} />
                    Networks
                  </ChakraLink>
                  <ChakraLink as={Link} to="/app/consultant/leads" onClick={onClose}>
                    <Icon as={FaUserFriends} mr={2} />
                    {t('navbar.leads', 'Leads')}
                  </ChakraLink>
                  <ChakraLink as={Link} to="/app/consultant/users" onClick={onClose}>
                    <Icon as={FaUsers} mr={2} />
                    {t('navbar.users')}
                  </ChakraLink>
                  {consultantAdminUser ? (
                    <ChakraLink as={Link} to="/app/consultant/consultant-settings" onClick={onClose}>
                      <Icon as={FaWrench} mr={2} />
                      Consultant Settings
                    </ChakraLink>
                  ) : null}
                </>
              ) : (
                <>
                  {isHkeAdmin ? (
                    <>
                      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                        {t('navbar.organizations_networks_group', 'Schools')}
                      </Text>
                      <ChakraLink as={Link} to="/app/organizations" onClick={onClose}>
                        <Icon as={FaUserTie} mr={2} />
                        {t('navbar.organization')}
                      </ChakraLink>
                      <ChakraLink as={Link} to="/app/admin/networks" onClick={onClose}>
                        <Icon as={FaBuilding} mr={2} />
                        Networks
                      </ChakraLink>
                    </>
                  ) : (
                    <ChakraLink as={Link} to="/app/organizations" onClick={onClose}>
                      <Icon as={FaUserTie} mr={2} />
                      {t('navbar.organization')}
                    </ChakraLink>
                  )}

                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                    {t('navbar.surveys_group', 'Surveys')}
                  </Text>
                  <ChakraLink as={Link} to="/app/surveys" onClick={onClose}>
                    <Icon as={FaClipboardList} mr={2} />
                    {t('navbar.surveys')}
                  </ChakraLink>
                  <ChakraLink as={Link} to="/app/survey_periods" onClick={onClose}>
                    <Icon as={FaRegCalendarAlt} mr={2} />
                    {t('navbar.survey_periods')}
                  </ChakraLink>

                  <ChakraLink as={Link} to="/app/messages" onClick={onClose}>
                    <Icon as={FaEnvelope} mr={2} />
                    {t('navbar.messages')}
                  </ChakraLink>
                  <ChakraLink as={Link} to="/app/users" onClick={onClose}>
                    <Icon as={FaUsers} mr={2} />
                    {t('navbar.users')}
                  </ChakraLink>

                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                    {t('navbar.resources_group', 'Resources')}
                  </Text>
                  <ChakraLink as={Link} to="/app/articles" onClick={onClose}>
                    <Icon as={FaNewspaper} mr={2} />
                    {t('navbar.articles', 'Articles')}
                  </ChakraLink>
                  <ChakraLink as={Link} to="/app/widget" onClick={onClose}>
                    <Icon as={FaPuzzlePiece} mr={2} />
                    {t('navbar.widgets', 'Widgets')}
                  </ChakraLink>

                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                    {t('navbar.consulting_group', 'Consulting')}
                  </Text>
                  {canManageConsultants ? (
                    <ChakraLink as={Link} to="/app/admin/consultants" onClick={onClose}>
                      <Icon as={FaBuilding} mr={2} />
                      {t('navbar.consultants', 'Consultants')}
                    </ChakraLink>
                  ) : null}
                  {canViewLeads ? (
                    <ChakraLink as={Link} to="/app/admin/leads" onClick={onClose}>
                      <Icon as={FaUserFriends} mr={2} />
                      {t('navbar.leads', 'Leads')}
                    </ChakraLink>
                  ) : null}
                  {canAccessAdmin && (
                    <ChakraLink as={Link} to="/app/admin" onClick={onClose}>
                      <Icon as={FaWrench} mr={2} />
                      {t('navbar.admin')}
                    </ChakraLink>
                  )}
                </>
              )}
              <Divider />
              <ChakraLink as={Link} to={`${appBasePath}/profile`} onClick={onClose}>
                <Icon as={FaUser} mr={2} />
                {t('navbar.profile')}
              </ChakraLink>
              <ChakraLink as={Link} to={`${appBasePath}/settings`} onClick={onClose}>
                <Icon as={FaUserCog} mr={2} />
                {t('navbar.preferences', 'Preferences')}
              </ChakraLink>
              <ChakraLink as={Link} to={`${appBasePath}/password`} onClick={onClose}>
                <Icon as={FaKey} mr={2} />
                {t('navbar.password')}
              </ChakraLink>
              <Divider />
              <ChakraLink as="button" onClick={handleLogout}>
                <Icon as={FaSignOutAlt} mr={2} />
                {t('navbar.logout')}
              </ChakraLink>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
};

export default Navbar;
